import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Button from "components/Button";
import CheckBox from "components/CheckBox";
import Tooltip from "components/Tooltip";
import Dropdown from "./Dropdown";
import Lottie from "lottie-react";
import { IconChevronDown, IconCredit, IconDownload } from "icons";
import { DOWNLOAD_SETTING, useLocalStorage } from "hooks/useLocalStorage";
import { useLoadingSpinner } from "hooks/useLoadingSpinner";
import { useAppSelector } from "store";
import { DownloadResolution, DOWNLOAD_4K_CREDIT_KEY, FileTypeOption, FILE_TYPE_OPTIONS, isRasterFormat, RESOLUTION_OPTIONS, VIDEO_FILE_TYPE_OPTION } from "../constants";
import { ImageDimensions } from "../hooks/useImageDimensions";

interface DownloadMenuProps {
  fileType: FileTypeOption;
  setFileType: (fileType: FileTypeOption) => void;
  onDownload: () => void;
  isDownloading: boolean;
  isSvgAvailable: boolean;
  svgComingSoon?: boolean;
  isVideo: boolean;
  resolution: DownloadResolution;
  setResolution: (resolution: DownloadResolution) => void;
  isTechpackDownload?: boolean;
  /** Native dimensions of imageURL, measured lazily on open. Drives the Original (W×H) label and the already-4K disable. */
  imageDimensions?: ImageDimensions;
  /** User-editable base file name (no extension). Prefilled from the image URL in DownloadButton. */
  filename: string;
  setFilename: (filename: string) => void;
  /** REF-5701: "Include AI Generated" toggle state + change handler (persisted in DownloadButton). */
  aiLabel: boolean;
  onAiLabelChange: (checked: boolean) => void;
}

const DownloadMenu: React.FC<DownloadMenuProps> = ({
  fileType,
  setFileType,
  onDownload,
  isDownloading,
  isSvgAvailable,
  svgComingSoon = false,
  isVideo,
  resolution,
  setResolution,
  isTechpackDownload = false,
  imageDimensions,
  filename,
  setFilename,
  aiLabel,
  onAiLabelChange,
}) => {
  const { t } = useTranslation();
  const loadingSpinner = useLoadingSpinner();
  const [isSaved, setIsSaved] = useState(useLocalStorage().getItem(DOWNLOAD_SETTING) === fileType.value);
  const { creditMultiplier } = useAppSelector((state) => state.credit);

  const _options = isVideo ? [VIDEO_FILE_TYPE_OPTION] : isSvgAvailable ? FILE_TYPE_OPTIONS : FILE_TYPE_OPTIONS.filter((option) => option.value !== "svg");

  // 4K upscale is png/jpg only (the BE crystal upscaler outputs png/jpg), never for webp/svg/video/
  // techpack, and hidden while the AI label is on — that path is Original-resolution, BE-processed. REF-5701.
  const showResolution = (fileType.value === "png" || fileType.value === "jpg") && !isTechpackDownload && !aiLabel;
  // The "Include AI Generated" toggle applies to raster downloads (png/jpg/webp); not svg/video.
  const showAiLabel = isRasterFormat(fileType.value) && !isVideo;
  // Unresolved key → "-" by design (Trap #7) until BE publishes the download_4k multiplier.
  const fourKCredit = creditMultiplier?.[DOWNLOAD_4K_CREDIT_KEY]?.need_credit ?? "-";

  const renderItem = (item: FileTypeOption) => {
    const Icon = item.icon;
    const isActive = item.value === fileType.value;
    const isSvgOption = item.value === "svg";
    const showSoonBadge = isSvgOption && svgComingSoon;
    const isDisabled = showSoonBadge;

    return (
      <li
        key={item.value}
        role="menuitem"
        tabIndex={isDisabled ? -1 : 0}
        className={clsx(
          "flex item-center p-[7px] border border-black-300 rounded",
          isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          isActive ? "bg-purpleGreen bg-opacity-10" : "bg-black-100"
        )}
        onClick={() => {
          if (isDisabled) return;
          setFileType(item);
          if (isSaved) useLocalStorage().setItem(DOWNLOAD_SETTING, item.value);
        }}
        onKeyDown={(e) => {
          if (isDisabled) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setFileType(item);
            if (isSaved) useLocalStorage().setItem(DOWNLOAD_SETTING, item.value);
          }
        }}
        aria-pressed={isActive}
        aria-disabled={isDisabled}
      >
        <div className="flex items-center w-full gap-2.5">
          <div className={clsx("rounded-full h-2.5 w-2.5", isActive ? "bg-purpleGreen" : "bg-black-300")} />
          <Icon className="text-white w-4 h-4" />
          <span className="text-white text-bodySm">{item.text}</span>
          {showSoonBadge && <span className="px-1.5 py-0.5 text-[10px] font-workSans text-black bg-purpleGreen rounded uppercase">{t("common:download_menu.soon_badge")}</span>}
          <span className="text-grey text-[11px] font-normal font-workSans leading-[15.4px] tracking-[-0.165px]">{item.description}</span>
        </div>
      </li>
    );
  };

  return (
    <div className="flex flex-col rounded-[5px] bg-body border border-black-300">
      <div className="flex gap-[5px] text-white p-2 bg-body-100 rounded-t-[4px]">
        <IconDownload className="w-[14px] h-[14px]" />
        <span className="text-headline uppercase">{t("common:download_menu.heading")}</span>
      </div>

      <div className="flex flex-col p-2.5 gap-2.5">
        <span className="text-h7 text-white">{t("common:download_menu.file_format")}</span>

        <Dropdown
          options={_options}
          isToggle={true}
          renderItem={renderItem}
          hideBorder={true}
          className="w-full gap-1 p-[7px]"
          role="listbox"
          aria-label={t("common:download_menu.select_file_format_aria")}
        >
          <div className="cursor-pointer flex border border-black-300 rounded w-full ">
            <div className="flex items-center gap-2.5 w-full p-[7px]">
              <fileType.icon className="text-white w-4 h-4" />
              <span className="text-bodySm text-white">{fileType.text}</span>
            </div>
            <div className="flex-center border-l border-black-300 p-[7px]">
              <IconChevronDown className="flex-shrink-0 w-[18px] h-[18px] text-white" />
            </div>
          </div>
        </Dropdown>

        {showResolution && (
          <>
            <span className="text-h7 text-white">{t("common:download_menu.resolution")}</span>
            <div className="flex flex-col gap-1.5" role="radiogroup" aria-label={t("common:download_menu.resolution")}>
              {RESOLUTION_OPTIONS.map((option) => {
                const isActive = option.value === resolution;
                const is4k = option.value === "4k";
                // The image's longest edge is already ≥4K → upscaling is a no-op; disable + tooltip (mirrors the BE already_4k).
                const isUpscaleDisabled = is4k && (imageDimensions?.isAlready4k ?? false);
                // Original row shows the measured native resolution once known; blank while measuring / on failure.
                const showDims = !is4k && imageDimensions?.status === "done" && !!imageDimensions.width && !!imageDimensions.height;
                const dimsLabel = showDims ? `(${imageDimensions?.width}×${imageDimensions?.height})` : "";

                const row = (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    aria-disabled={isUpscaleDisabled}
                    onClick={() => {
                      if (isUpscaleDisabled) return;
                      setResolution(option.value);
                    }}
                    className={clsx(
                      "w-full flex items-center gap-2.5 p-[7px] border rounded",
                      isUpscaleDisabled
                        ? "cursor-not-allowed opacity-50 border-black-300 bg-black-100"
                        : isActive
                        ? "cursor-pointer border-purpleGreen bg-purpleGreen bg-opacity-10"
                        : "cursor-pointer border-black-300 bg-black-100 hover:bg-black-200"
                    )}
                  >
                    <div className={clsx("rounded-full h-2.5 w-2.5 flex-shrink-0", isActive ? "bg-purpleGreen" : "bg-black-300")} />
                    <span className="text-bodySm text-white">{t(option.labelKey)}</span>
                    {showDims && <span className="ml-auto text-grey text-[11px] font-workSans">{dimsLabel}</span>}
                    {is4k && !isUpscaleDisabled && (
                      <span className="ml-auto flex items-center gap-0.5 text-grey text-[11px] font-workSans">
                        <IconCredit className="w-3 h-3 flex-shrink-0" />
                        {fourKCredit}
                      </span>
                    )}
                  </button>
                );

                // Tooltip wraps the upscale row; `active` gates it to the disabled state only (hover still works
                // because the row uses aria-disabled, not native disabled, so it keeps receiving mouse events).
                return is4k ? (
                  <Tooltip key={option.value} content={t("common:download_menu.resolution_already_4k_tooltip")} active={isUpscaleDisabled} position="top" className="w-full">
                    {row}
                  </Tooltip>
                ) : (
                  row
                );
              })}
            </div>
          </>
        )}

        <span className="text-h7 text-white">{t("common:download_menu.file_name")}</span>

        <input
          type="text"
          value={filename}
          onChange={(e) => setFilename(e.target.value)}
          placeholder={t("common:download_menu.file_name_placeholder")}
          aria-label={t("common:download_menu.file_name")}
          className="w-full p-[7px] border border-black-300 rounded bg-black-100 text-bodySm text-white placeholder:text-grey focus:outline-none focus:border-purpleGreen"
        />

        {showAiLabel && (
          <>
            <div className="flex w-full h-[1px] bg-black-300" />
            <div className="flex flex-col gap-1">
              <CheckBox
                checked={aiLabel}
                checkedColor="bg-purpleGreen"
                boxSize="lg"
                containerClassName="cursor-pointer flex items-center gap-2.5 w-fit"
                onChange={(_, isChecked: boolean) => onAiLabelChange(isChecked)}
              >
                <span className="text-bodySm text-white">{t("common:download_menu.ai_label_toggle")}</span>
              </CheckBox>
              <span className="text-grey text-[11px] font-normal font-workSans leading-[15.4px] pl-[23px]">{t("common:download_menu.ai_label_hint")}</span>
            </div>
          </>
        )}

        {fileType.value !== "svg" && !isVideo && (
          <>
            <div className="flex w-full h-[1px] bg-black-300" />
            <CheckBox
              checked={isSaved}
              checkedColor="bg-purpleGreen"
              boxSize="lg"
              containerClassName="cursor-pointer flex items-center gap-2.5 w-fit"
              onChange={(_, isChecked: boolean) => {
                setIsSaved(isChecked);
                if (isChecked) useLocalStorage().setItem(DOWNLOAD_SETTING, fileType.value);
                else useLocalStorage().removeItem(DOWNLOAD_SETTING);
              }}
            >
              <span className="text-bodySm text-white">{t("common:download_menu.save_settings")}</span>
            </CheckBox>
          </>
        )}
      </div>

      <div className="flex-center border-t border-black-300 p-2.5 bg-black-100 rounded-b-[4px]">
        {isDownloading ? (
          <Lottie animationData={loadingSpinner} loop={true} className="w-8 h-8" />
        ) : (
          <Button
            className="btn-primary btn-sm w-full !bg-purpleGreen"
            onClick={() => {
              onDownload();
            }}
            aria-label={t("common:download_menu.download_aria")}
          >
            {t("common:download_menu.button")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DownloadMenu;
